import { AlertTriangle, Clock, Info, Plus, Trash2, CheckCircle } from 'lucide-react';
import React, { useState } from 'react';

import { useOnboardingStore } from '../../../store/onboarding.store';

interface LateFeesStepProps {
  onNext: () => void;
  onBack: () => void;
}

interface StagedFee {
  days: number;
  amount: number;
}

export function LateFeesStep({ onNext, onBack }: LateFeesStepProps) {
  const { billingData, setBillingData, setCurrentBillingStep } = useOnboardingStore();
  // Note: 'self-managed' in store maps to 'self-managed' in database
  const isManualBilling = billingData.provider === 'self-managed';
  
  const [stagedFees, setStagedFees] = useState<StagedFee[]>(
    Array.isArray(billingData.lateFeeConfig) 
      ? billingData.lateFeeConfig as StagedFee[]
      : [
          { days: 5, amount: 5 },
          { days: 10, amount: 10 },
          { days: 15, amount: 15 },
        ]
  );
  const [maxFeeAmount, setMaxFeeAmount] = useState(
    billingData.lateFeeConfig && !Array.isArray(billingData.lateFeeConfig)
      ? (billingData.lateFeeConfig as { max_amount: number }).max_amount
      : 25
  );
  const [daysUntilCancellation, setDaysUntilCancellation] = useState(
    billingData.lateFeeConfig && !Array.isArray(billingData.lateFeeConfig)
      ? (billingData.lateFeeConfig as { days_until_cancellation: number }).days_until_cancellation
      : 30
  );

  const handleSelectStrategy = (strategy: 'staged' | 'max_total') => {
    setBillingData({ lateFeeStrategy: strategy });
    if (strategy === 'staged') {
      setBillingData({ lateFeeConfig: stagedFees });
    } else {
      setBillingData({ lateFeeConfig: { max_amount: maxFeeAmount, days_until_cancellation: daysUntilCancellation } });
    }
  };

  const addStagedFee = () => {
    const lastFee = stagedFees[stagedFees.length - 1];
    const newFee = { days: lastFee.days + 5, amount: lastFee.amount + 5 };
    const newFees = [...stagedFees, newFee];
    setStagedFees(newFees);
    setBillingData({ lateFeeConfig: newFees });
  };

  const removeStagedFee = (index: number) => {
    if (stagedFees.length > 1) {
      const newFees = stagedFees.filter((_, i) => i !== index);
      setStagedFees(newFees);
      setBillingData({ lateFeeConfig: newFees });
    }
  };

  const updateStagedFee = (index: number, field: 'days' | 'amount', value: number) => {
    const newFees = [...stagedFees];
    newFees[index][field] = value;
    setStagedFees(newFees);
    setBillingData({ lateFeeConfig: newFees });
  };

  // For self-managed billing, show different content focused on terms & conditions
  if (isManualBilling) {
    return (
      <div className="space-y-6">
        <div className="text-center mb-6">
          <h2 className="text-xl font-bold text-[#EBFFEE]">Self-Managed Billing</h2>
          <p className="text-[#D1D5DB] mt-2">
            Configure terms and conditions for manual payment management
          </p>
        </div>

        {/* Manual Billing Info Card */}
        <div className="bg-[#363232] rounded-xl p-5 border border-[#504A4A]">
          <div className="flex items-start gap-3 mb-4">
            <Info size={24} className="text-rollops-accent flex-shrink-0" />
            <div>
              <h3 className="font-semibold text-[#EBFFEE] mb-2">How Self-Managed Billing Works</h3>
              <p className="text-sm text-[#D1D5DB] mb-4">
                With self-managed billing, you handle all payments directly with your members. RollOps will help you stay in sync with the application.
              </p>
              
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <CheckCircle size={18} className="text-green-500 flex-shrink-0" />
                  <span className="text-sm text-[#D1D5DB]">
                    Students can enroll in subscriptions to access classes and features
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle size={18} className="text-green-500 flex-shrink-0" />
                  <span className="text-sm text-[#D1D5DB]">
                    You can manually enable/disable access based on payment status
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle size={18} className="text-green-500 flex-shrink-0" />
                  <span className="text-sm text-[#D1D5DB]">
                    Daily report emails will help you stay in sync with what needs to happen
                  </span>
                </div>
              </div>
            </div>
          </div>
          
          <div className="mt-4 pt-4 border-t border-[#504A4A]">
            <p className="text-sm text-[#D1D5DB] mb-3">
              Want us to handle payments instead? You can switch to RollOps-managed billing anytime from your billing settings.
            </p>
            <button
              onClick={() => {
                setBillingData({ provider: 'rollops' });
                // Navigate back to provider step to select RollOps
                setCurrentBillingStep('has-payment-provider');
              }}
              className="w-full py-3 px-4 bg-[#9B1421] hover:bg-[#7A0F1A] border border-[#9B1421] text-white font-semibold rounded-lg transition-colors flex items-center justify-center gap-2"
            >
              Switch to RollOps Billing
            </button>
          </div>
        </div>

        {/* Terms & Conditions Section */}
        <div className="bg-[#363232] rounded-xl p-5 border border-[#504A4A]">
          <h3 className="font-semibold text-[#EBFFEE] mb-2">Terms & Conditions</h3>
          <p className="text-sm text-[#D1D5DB] mb-4">
            Add terms and conditions that students will see when signing up for subscriptions on your app. This helps set expectations about payment, access, and billing.
          </p>
          <textarea
            value={billingData.additionalTerms}
            onChange={(e) => setBillingData({ additionalTerms: e.target.value })}
            placeholder="Enter terms and conditions for subscriptions (e.g., payment expectations, access rules, billing policies)..."
            rows={8}
            className="w-full px-3 py-2 rounded-lg bg-[#504A4A] text-[#EBFFEE] placeholder:text-[#D1D5DB] border-none outline-none focus:ring-2 focus:ring-[#9B1421]/50 resize-none"
          />
        </div>

        {/* Navigation Buttons */}
        <div className="flex gap-4 pt-4">
          <button
            onClick={onBack}
            className="flex-1 py-3 px-4 bg-[#504A4A] hover:bg-[#5A5454] text-white font-semibold rounded-lg transition-colors"
          >
            Back
          </button>
          <button
            onClick={onNext}
            className="flex-1 py-3 px-4 bg-[#9B1421] hover:bg-[#7A0F1A] text-white font-semibold rounded-lg transition-colors"
          >
            Continue
          </button>
        </div>
      </div>
    );
  }

  // For RollOps billing, show the full late fees configuration
  return (
    <div className="space-y-6">
      <div className="text-center mb-6">
        <h2 className="text-xl font-bold text-[#EBFFEE]">Late Fees & Cancellation</h2>
        <p className="text-[#D1D5DB] mt-2">
          Set up late fee policies for overdue payments
        </p>
      </div>

      {/* Late Fee Strategy Selection */}
      <div className="space-y-4">
        {/* Staged Fees Option */}
        <button
          onClick={() => handleSelectStrategy('staged')}
          className={`w-full p-4 rounded-xl border-2 text-left transition-all ${
            billingData.lateFeeStrategy === 'staged'
              ? 'border-[#9B1421] bg-[#9B1421]/10'
              : 'border-[#504A4A] hover:border-[#9B1421]/50 bg-[#363232]'
          }`}
        >
          <div className="flex items-center gap-3">
            <div
              className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                billingData.lateFeeStrategy === 'staged'
                  ? 'bg-[#9B1421]'
                  : 'bg-[#504A4A]'
              }`}
            >
              <Clock size={20} className={billingData.lateFeeStrategy === 'staged' ? 'text-white' : 'text-[#D1D5DB]'} />
            </div>
            <div>
              <h3 className="font-semibold text-[#EBFFEE]">Staged Late Fees</h3>
              <p className="text-sm text-[#D1D5DB]">
                Fees increase over time based on days overdue
              </p>
            </div>
          </div>
        </button>

        {/* Max Total Option */}
        <button
          onClick={() => handleSelectStrategy('max_total')}
          className={`w-full p-4 rounded-xl border-2 text-left transition-all ${
            billingData.lateFeeStrategy === 'max_total'
              ? 'border-[#9B1421] bg-[#9B1421]/10'
              : 'border-[#504A4A] hover:border-[#9B1421]/50 bg-[#363232]'
          }`}
        >
          <div className="flex items-center gap-3">
            <div
              className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                billingData.lateFeeStrategy === 'max_total'
                  ? 'bg-[#9B1421]'
                  : 'bg-[#504A4A]'
              }`}
            >
              <AlertTriangle size={20} className={billingData.lateFeeStrategy === 'max_total' ? 'text-white' : 'text-[#D1D5DB]'} />
            </div>
            <div>
              <h3 className="font-semibold text-[#EBFFEE]">Maximum Total Fee</h3>
              <p className="text-sm text-[#D1D5DB]">
                One-time fee up to a maximum amount
              </p>
            </div>
          </div>
        </button>
      </div>

      {/* Staged Fees Configuration */}
      {billingData.lateFeeStrategy === 'staged' && (
        <div className="bg-[#363232] rounded-xl p-4 space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="font-medium text-[#EBFFEE]">Fee Schedule</h3>
            <button
              onClick={addStagedFee}
              className="text-sm text-white hover:text-green-400 flex items-center gap-1 px-3 py-1.5 rounded-lg bg-[#9B1421] hover:bg-[#7A0F1A] transition-colors"
            >
              <Plus size={14} />
              Add Stage
            </button>
          </div>
          
          <div className="space-y-3">
            {stagedFees.map((fee, index) => (
              <div key={index} className="flex items-center gap-3">
                <div className="flex-1">
                  <label className="block text-xs text-[#D1D5DB] mb-1">Days Overdue</label>
                  <input
                    type="number"
                    value={fee.days}
                    onChange={(e) => updateStagedFee(index, 'days', parseInt(e.target.value) || 0)}
                    min={1}
                    className="w-full px-3 py-2 rounded-lg bg-[#504A4A] text-[#EBFFEE] border-none outline-none focus:ring-2 focus:ring-[#9B1421]/50 text-sm"
                  />
                </div>
                <div className="flex-1">
                  <label className="block text-xs text-[#D1D5DB] mb-1">Fee Amount ($)</label>
                  <input
                    type="number"
                    value={fee.amount}
                    onChange={(e) => updateStagedFee(index, 'amount', parseInt(e.target.value) || 0)}
                    min={0}
                    className="w-full px-3 py-2 rounded-lg bg-[#504A4A] text-[#EBFFEE] border-none outline-none focus:ring-2 focus:ring-[#9B1421]/50 text-sm"
                  />
                </div>
                {stagedFees.length > 1 && (
                  <button
                    onClick={() => removeStagedFee(index)}
                    className="mt-5 p-2 text-[#D1D5DB] hover:text-red-500 transition-colors"
                  >
                    <Trash2 size={16} />
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Max Total Fee Configuration */}
      {billingData.lateFeeStrategy === 'max_total' && (
        <div className="bg-[#363232] rounded-xl p-4 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-[#EBFFEE] mb-2">
                Maximum Fee ($)
              </label>
              <input
                type="number"
                value={maxFeeAmount}
                onChange={(e) => {
                  const value = parseInt(e.target.value) || 0;
                  setMaxFeeAmount(value);
                  setBillingData({ lateFeeConfig: { max_amount: value, days_until_cancellation: daysUntilCancellation } });
                }}
                min={0}
                className="w-full px-3 py-2 rounded-lg bg-[#504A4A] text-[#EBFFEE] border-none outline-none focus:ring-2 focus:ring-[#9B1421]/50"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-[#EBFFEE] mb-2">
                Days Until Auto-Cancel
              </label>
              <input
                type="number"
                value={daysUntilCancellation}
                onChange={(e) => {
                  const value = parseInt(e.target.value) || 0;
                  setDaysUntilCancellation(value);
                  setBillingData({ lateFeeConfig: { max_amount: maxFeeAmount, days_until_cancellation: value } });
                }}
                min={1}
                className="w-full px-3 py-2 rounded-lg bg-[#504A4A] text-[#EBFFEE] border-none outline-none focus:ring-2 focus:ring-[#9B1421]/50"
              />
            </div>
          </div>
        </div>
      )}

      {/* Cancellation Notice Period */}
      <div className="bg-[#363232] rounded-xl p-4">
        <label className="block text-sm font-medium text-[#EBFFEE] mb-2">
          Cancellation Notice Period (Days)
        </label>
        <input
          type="number"
          value={billingData.cancellationNoticeDays}
          onChange={(e) => setBillingData({ cancellationNoticeDays: parseInt(e.target.value) || 0 })}
          min={0}
          className="w-full px-3 py-2 rounded-lg bg-[#504A4A] text-[#EBFFEE] border-none outline-none focus:ring-2 focus:ring-[#9B1421]/50"
        />
        <p className="text-xs text-[#D1D5DB] mt-2">
          Number of days notice required before a member can cancel their subscription
        </p>
      </div>

      {/* Additional Terms */}
      <div className="bg-[#363232] rounded-xl p-4">
        <label className="block text-sm font-medium text-[#EBFFEE] mb-2">
          Additional Terms & Conditions (Optional)
        </label>
        <textarea
          value={billingData.additionalTerms}
          onChange={(e) => setBillingData({ additionalTerms: e.target.value })}
          placeholder="Enter any additional billing terms or policies..."
          rows={4}
          className="w-full px-3 py-2 rounded-lg bg-[#504A4A] text-[#EBFFEE] placeholder:text-[#D1D5DB] border-none outline-none focus:ring-2 focus:ring-[#9B1421]/50 resize-none"
        />
      </div>

      {/* Info Box */}
      <div className="flex items-start gap-3 bg-blue-500/10 border border-blue-500/30 rounded-lg p-4">
        <Info size={20} className="text-blue-400 flex-shrink-0 mt-0.5" />
        <div>
          <p className="text-sm text-blue-400 font-medium">Late Fee Best Practices</p>
          <p className="text-xs text-[#D1D5DB] mt-1">
            Most gyms use a grace period of 5 days before applying late fees. Clear communication 
            of your policies helps maintain good member relationships.
          </p>
        </div>
      </div>

      {/* Navigation Buttons */}
      <div className="flex gap-4 pt-4">
        <button
          onClick={onBack}
          className="flex-1 py-3 px-4 bg-[#504A4A] hover:bg-[#5A5454] text-white font-semibold rounded-lg transition-colors"
        >
          Back
        </button>
        <button
          onClick={onNext}
          disabled={!isManualBilling && !billingData.lateFeeStrategy}
          className="flex-1 py-3 px-4 bg-[#9B1421] hover:bg-[#7A0F1A] disabled:bg-[#504A4A] disabled:cursor-not-allowed text-white font-semibold rounded-lg transition-colors"
        >
          Continue
        </button>
      </div>
    </div>
  );
}

